import type { NodeV2 } from '@vedalib/editor'
import { DeviceMode, isNodeV2 } from '@vedalib/editor'
import * as R from 'ramda'

import { getParentNode } from 'components/Editor/EditorContext/selectors/block'
import { notify } from 'components/uiKit/Notification'
import { NotificationType } from 'components/uiKit/Notification/types'
import { setProjectNavigation, updateBlock } from 'services/Store/Project/actions'
import { addNode, updateCoordinates, setGridArea } from 'services/Store/Project/mutators/node'
import {
  getBlock,
  getDeviceMode,
  getSelectedNodeIds,
  mergeNodeStyle,
} from 'services/Store/Project/selectors'
import type { Block } from 'services/Store/Project/types'
import store from 'services/Store/store'
import { t } from 'services/Translation'

import { collectDndData } from '../DisplayProvider/utils'
import { remapComplexNode, type IComplexNode } from '../Picker/complexNodes'
import { safeParseArea } from '../formControls/Area/utils'
import { safeParseGrid } from '../formControls/Grid/utils'
import { isAbsolute, isGrid } from '../guards'

export const onPaste = async (event: KeyboardEvent) => {
  const state = store.getState().project
  const dispatch = store.dispatch
  const block = getBlock(state)
  const selectedNodes = getSelectedNodeIds(state)
  const node = selectedNodes.length === 1 ? block?.schema.nodes[selectedNodes[0]] : null
  const parent = getParentNode(node?.id, block)
  const device = getDeviceMode(state)
  const data: IComplexNode[] = JSON.parse(await navigator.clipboard.readText()).map(
    (item: IComplexNode) => remapComplexNode(item),
  )
  const dest = block && collectDndData(block, state.state.editor.coordinates)
  const isDesktop = device === DeviceMode.desktop

  if (
    isDesktop &&
    block &&
    dest?.destinationId &&
    data.length &&
    isNodeV2(node) &&
    !node.elementId
  ) {
    event.preventDefault()
    const destNode = block.schema.nodes[dest.destinationId] as NodeV2 | null
    const destStyle = mergeNodeStyle(destNode?.style || {}, device)
    const cols = safeParseGrid(destStyle.gridColumns).items.length
    const colSize = R.clamp(2, Math.max(2, cols), Math.floor((cols - 2) / 2))

    const cbs = data.map(({ root, elements, nodes }) => {
      if (destNode) {
        return R.compose(
          isGrid(destStyle)
            ? setGridArea(root.id, device, {
                rowStart: dest.row || 1,
                colStart: dest.col || 1,
                rowEnd: (dest.row || 1) + 1,
                colEnd: (dest.col || 1) + colSize,
              })
            : R.identity<Block>,
          isAbsolute(destStyle)
            ? updateCoordinates(root.id, device, { top: dest.top, left: dest.left })
            : R.identity<Block>,
          addNode(destNode.id, root, { elements, nodes }, { index: dest.insertIndex }),
        )
      }
      return R.identity<Block>
    }) as [(block: Block) => Block]

    dispatch(updateBlock({ id: block.uuid, value: R.compose(...cbs)(block) }))
    dispatch(setProjectNavigation({ nodeIds: data.map(({ root }) => root.id) }))
  } else if (block && isNodeV2(parent) && isNodeV2(node)) {
    const destNode = block.schema.nodes[parent.id] as NodeV2 | null
    const destStyle = mergeNodeStyle(destNode?.style || {}, device)
    const style = mergeNodeStyle(node.style || {}, device)
    const area = safeParseArea(style.gridArea)
    const index = parent.children.indexOf(node.id)
    const cbs = data.map(({ root, elements, nodes }) => {
      if (destNode) {
        return R.compose(
          destStyle.display === 'grid'
            ? setGridArea(root.id, device, {
                rowStart: area.rowStart + 1,
                colStart: area.colStart,
                rowEnd: area.rowEnd + 1,
                colEnd: area.colEnd,
              })
            : R.identity,
          addNode(destNode.id, root, { elements, nodes }, { index }),
        )
      }
      return R.identity<Block>
    }) as [(block: Block) => Block]

    dispatch(updateBlock({ id: block.uuid, value: R.compose(...cbs)(block) }))
    dispatch(setProjectNavigation({ nodeIds: data.map(({ root }) => root.id) }))
  } else {
    notify({
      type: NotificationType.error,
      message: t('notify.pasteNodeError'),
      duration: 2000,
    })
  }
}
